import React, { useState } from 'react';
import axios from 'axios';

// Keep API config consistent across components (same pattern as AddPaymentForm.jsx)
const API_BASE = (window.__APP_CONFIG__ && window.__APP_CONFIG__.API_URL) || process.env.REACT_APP_API_URL || '';
if (API_BASE && !axios.defaults.baseURL) {
  axios.defaults.baseURL = API_BASE;
}

// Admin token (optional depending on backend). Keep support for both token and password style keys.
const ADMIN_TOKEN = (window.__APP_CONFIG__ && (window.__APP_CONFIG__.ADMIN_TOKEN || window.__APP_CONFIG__.ADMIN_PASSWORD))
  ? (window.__APP_CONFIG__.ADMIN_TOKEN || window.__APP_CONFIG__.ADMIN_PASSWORD)
  : (process.env.REACT_APP_ADMIN_TOKEN || '');

const AddCourseForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    feeType: '', // 'monthly' or 'onetime'
    admissionFee: '',
    monthlyFee: '',
    onetimeFee: '',
    duration: '',
    teacher: '',
    organization: '',
    mode: 'offline',
    status: 'active'
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.feeType) {
      alert("Please fill the required fields: Course Name and Fee Type.");
      return;
    }
    if (!formData.teacher || !formData.organization) {
      alert('Please select Instructor/Teacher and Organization.');
      return;
    }

    if (formData.feeType === 'monthly') {
      const a = Number(formData.admissionFee);
      const m = Number(formData.monthlyFee);
      if (!Number.isFinite(a) || a <= 0 || !Number.isFinite(m) || m <= 0) {
        alert('For Monthly courses, please enter valid Admission Fee and Monthly Fee amounts.');
        return;
      }
    }

    if (formData.feeType === 'onetime') {
      const o = Number(formData.onetimeFee);
      if (!Number.isFinite(o) || o <= 0) {
        alert('For One-time courses, please enter a valid One-time Full Fee amount.');
        return;
      }
    }

    const payload = {
      name: formData.name.trim(),
      description: (formData.description || '').trim(),
      feeType: formData.feeType,
      teacher: formData.teacher,
      organization: formData.organization,
      mode: formData.mode || 'offline',
      status: formData.status || 'active',
      // fee fields
      admissionFee: formData.feeType === 'monthly' ? Number(formData.admissionFee) : undefined,
      monthlyFee: formData.feeType === 'monthly' ? Number(formData.monthlyFee) : undefined,
      onetimeFee: formData.feeType === 'onetime' ? Number(formData.onetimeFee) : undefined,
      duration: formData.duration !== '' ? Number(formData.duration) : undefined,
    };

    // Remove undefined fields so backend validators don't see them
    Object.keys(payload).forEach((k) => payload[k] === undefined && delete payload[k]);

    if (!API_BASE && !axios.defaults.baseURL) {
      console.error('API base URL is not configured. Set window.__APP_CONFIG__.API_URL or REACT_APP_API_URL.');
    }

    try {
      const res = await axios.post('/api/courses', payload, {
        headers: ADMIN_TOKEN ? { Authorization: `Bearer ${ADMIN_TOKEN}` } : undefined,
      });
      alert(`✅ Course added: ${res.data.course?.name || "Course"}`);
      setFormData({
        name: '',
        description: '',
        feeType: '',
        admissionFee: '',
        monthlyFee: '',
        onetimeFee: '',
        duration: '',
        teacher: '',
        organization: '',
        mode: 'offline',
        status: 'active'
      });
    } catch (err) {
      console.error("❌ Error saving course:", err.response?.data || err.message);
      alert(`Failed to save course: ${err.response?.data?.error || err.response?.data?.message || err.message}`);
    }
  };

  return (
    <div className="max-w-2xl mx-auto bg-white p-6 shadow rounded mt-10">
      <h2 className="text-xl font-semibold mb-4">Add New Course</h2>
      <form onSubmit={handleSubmit} className="space-y-4">

        <input
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
          placeholder="Course Name"
          className="border p-2 rounded w-full"
          required
        />

        <textarea
          name="description"
          value={formData.description}
          onChange={handleChange}
          placeholder="Course Description"
          className="border p-2 rounded w-full"
        />

        {/* Dropdown: Teacher */}
        <select
          name="teacher"
          value={formData.teacher}
          onChange={handleChange}
          className="border p-2 rounded w-full"
          required
        >
          <option value="">Select Instructor / Teacher</option>
          <option value="Ankita Roy">Ankita Roy</option>
          <option value="Tousif Haque">Tousif Haque</option>
          <option value="Mousumi Naskar">Mousumi Naskar</option>
          <option value="Saikat Mondal">Saikat Mondal</option>
                    <option value="Souvik Das">Souvik Das</option>
                              <option value="Meghna Dutta">Meghna Dutta</option>
                                        <option value="Snighdha Goswami">Snighdha Goswami</option>



        </select>

        {/* Dropdown: Organization */}
        <select
          name="organization"
          value={formData.organization}
          onChange={handleChange}
          className="border p-2 rounded w-full"
          required
        >
          <option value="">Select Organization / Branch</option>
          <option value="COG">COG</option>
          <option value="Salankaraa">Salankaraa</option>
        </select>

        {/* Dropdown: Fee Type */}
        <select
          name="feeType"
          value={formData.feeType}
          onChange={handleChange}
          className="border p-2 rounded w-full"
          required
        >
          <option value="">Select Fee Type</option>
          <option value="monthly">Monthly Fee + Admission</option>
          <option value="onetime">One-time Full Fee</option>
        </select>

        {/* Conditional Fee Fields */}
        {formData.feeType === 'monthly' && (
          <>
            <input
              type="number"
              name="admissionFee"
              value={formData.admissionFee}
              onChange={handleChange}
              placeholder="Admission Fee (₹)"
              className="border p-2 rounded w-full"
            />
            <input
              type="number"
              name="monthlyFee"
              value={formData.monthlyFee}
              onChange={handleChange}
              placeholder="Monthly Fee (₹)"
              className="border p-2 rounded w-full"
            />
          </>
        )}

        {formData.feeType === 'onetime' && (
          <input
            type="number"
            name="onetimeFee"
            value={formData.onetimeFee}
            onChange={handleChange}
            placeholder="One-time Full Fee (₹)"
            className="border p-2 rounded w-full"
          />
        )}

        {/* Duration */}
        <input
          type="number"
          name="duration"
          value={formData.duration}
          onChange={handleChange}
          placeholder="Duration (months)"
          className="border p-2 rounded w-full"
        />

        {/* Dropdown: Mode */}
        <select
          name="mode"
          value={formData.mode}
          onChange={handleChange}
          className="border p-2 rounded w-full"
        >
          <option value="offline">Offline</option>
          <option value="online">Online</option>
        </select>

        {/* Status (Optional if needed) */}
        {/* <select
          name="status"
          value={formData.status}
          onChange={handleChange}
          className="border p-2 rounded w-full"
        >
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select> */}

        <button
          type="submit"
          className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
        >
          Save Course
        </button>
      </form>
    </div>
  );
};

export default AddCourseForm;